import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const tiers = [
  { amount: '1 000', label: 'Pionnier', icon: 'Zap', highlight: false, free: false },
  { amount: '2 500', label: 'Bâtisseur', icon: 'Star', highlight: true, free: false },
  { amount: '5 000', label: 'Visionnaire', icon: 'Crown', highlight: false, free: false },
  { amount: 'Libre', label: 'Partenaire', icon: 'Heart', highlight: false, free: true },
];

const ContributionFormModal = ({ selectedTier, onClose, onSuccess }) => {
  const tier = tiers?.[selectedTier];
  const [formData, setFormData] = useState({ fullName: '', email: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e?.target || {};
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { error: dbError } = await supabase?.from('revolution_leads')?.insert({
          full_name: formData?.fullName,
          email: formData?.email,
          phone: formData?.phone || null,
          tier_label: tier?.label,
          tier_amount: tier?.free ? 'Libre' : tier?.amount,
        });

      if (dbError) throw dbError;

      // Send confirmation email (non-blocking)
      try {
        await supabase?.functions?.invoke('send-revolution-confirmation', {
          body: {
            full_name: formData?.fullName,
            email: formData?.email,
            tier_label: tier?.label,
            tier_amount: tier?.free ? 'Libre' : tier?.amount,
          },
        });
      } catch (_emailErr) {
        // Email failure is non-critical
      }

      // Trigger nurture sequence step 1 (non-blocking)
      try {
        await supabase?.functions?.invoke('send-nurture-sequence', {
          body: {
            full_name: formData?.fullName,
            email: formData?.email,
            tier_label: tier?.label,
            tier_amount: tier?.free ? 'Libre' : tier?.amount,
            sequence_step: 1,
          },
        });
      } catch (_nurtureErr) {
        // Nurture sequence failure is non-critical
      }

      onSuccess?.(formData?.fullName);
    } catch (err) {
      setError(err?.message || 'Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  // Backdrop click handler
  const handleBackdropClick = (e) => {
    if (e?.target === e?.currentTarget) onClose?.();
  };

  return (
    <div
      onClick={handleBackdropClick}
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fade-in"
    >
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .animate-fade-in { animation: fadeIn 0.3s ease-out; }
        @keyframes slideUpModal { from { opacity: 0; transform: translateY(30px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
        .animate-slide-up-modal { animation: slideUpModal 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
      `}</style>

      <div className="w-full max-w-[440px] bg-[#111111] rounded-3xl shadow-2xl overflow-hidden border border-white/10 animate-slide-up-modal">
        
        {/* Header */}
        <div className="p-8 border-b border-white/5 relative">
          <div className="flex items-start justify-between gap-4">
            <div>
              {/* Tier badge */}
              <div className="inline-flex items-center gap-2 bg-brand-gold/10 px-4 py-1.5 rounded-full mb-6 border border-brand-gold/20">
                <Icon name={tier?.icon} size={14} className="text-brand-gold" strokeWidth={2} />
                <span className="text-brand-gold text-[10px] font-bold tracking-widest uppercase">
                  {tier?.label} · {tier?.free ? 'Libre' : `${tier?.amount} XOF`}
                </span>
              </div>
              <h2 className="text-2xl font-heading font-bold text-white tracking-tight leading-snug">
                Finalisez votre<br />
                <span className="text-brand-gold">engagement</span>
              </h2>
            </div>
            
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors border-none outline-none flex-shrink-0"
            >
              <Icon name="X" size={16} className="text-white/60" strokeWidth={2.5} />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-8 bg-[#0A0A0A]">
          <p className="text-sm font-light text-white/50 leading-relaxed mb-8">
            Laissez-nous vos coordonnées et notre équipe vous contactera pour finaliser votre participation.
          </p>

          <div className="space-y-5">
            {/* Full name */}
            <div>
              <label className="block text-[10px] font-bold text-white/40 uppercase tracking-widest mb-2">Nom complet *</label>
              <input
                type="text"
                name="fullName"
                value={formData?.fullName}
                onChange={handleChange}
                required
                placeholder="Votre nom complet"
                className="w-full px-5 py-4 bg-white/5 border-none rounded-xl text-white placeholder-white/20 focus:ring-2 focus:ring-brand-gold focus:outline-none transition-all shadow-inner"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-[10px] font-bold text-white/40 uppercase tracking-widest mb-2">Adresse email *</label>
              <input
                type="email"
                name="email"
                value={formData?.email}
                onChange={handleChange}
                required
                placeholder="votre@email.com"
                className="w-full px-5 py-4 bg-white/5 border-none rounded-xl text-white placeholder-white/20 focus:ring-2 focus:ring-brand-gold focus:outline-none transition-all shadow-inner"
              />
            </div>

            {/* Phone */}
            <div>
              <label className="block text-[10px] font-bold text-white/40 uppercase tracking-widest mb-2">
                Téléphone <span className="font-light normal-case tracking-normal opacity-50">(optionnel)</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData?.phone}
                onChange={handleChange}
                placeholder="+225 XX XX XX XX XX"
                className="w-full px-5 py-4 bg-white/5 border-none rounded-xl text-white placeholder-white/20 focus:ring-2 focus:ring-brand-gold focus:outline-none transition-all shadow-inner"
              />
            </div>
          </div>

          {error && (
            <div className="mt-6 p-4 bg-red-500/10 rounded-xl flex items-start gap-3">
              <Icon name="AlertCircle" size={18} className="text-red-400 mt-0.5" />
              <p className="text-sm font-medium text-red-400">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className={`w-full mt-8 py-4 flex items-center justify-center gap-3 font-bold rounded-xl transition-all border-none ${
              loading 
                ? 'bg-brand-gold/50 text-black/50 cursor-default' 
                : 'bg-brand-gold text-black hover:bg-yellow-400 hover:-translate-y-0.5 shadow-lg shadow-brand-gold/20'
            }`}
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                Envoi en cours…
              </>
            ) : (
              <>
                <Icon name="Rocket" size={18} strokeWidth={2.5} />
                Rejoindre la révolution
              </>
            )}
          </button>

          <p className="text-center mt-6 text-[10px] font-bold uppercase tracking-widest text-white/20">
            Données confidentielles et sécurisées.
          </p>
        </form>
      </div>
    </div>
  );
};

export default ContributionFormModal;